<script setup>
import { humanReadableTimestamp, humanReadableDuration, parseDuration } from '@/composables/datetime'
import { useI18n } from 'vue-i18n'
import { computed, ref } from 'vue'
import { useCameraName } from '@/composables/cameraHelpers'
import ArtifactPayload from '@/components/artifacts/ArtifactPayload.vue'
import { humanReadableFilesize } from '@/composables/filesizeHelpers.js'
import { useInstanceStore } from '@/store/backendInstances'
import ShareButton from '@/components/ShareButton.vue'
import { writeToClipboard } from '@/composables/clipboard.ts'
import { useToast } from 'vue-toastification'
import { getOrigin } from '@/composables/version.js'
import GenericCustomDialog from '@/components/GenericCustomDialog.vue'
import { useDisplay } from 'vuetify'
import { useArtifactStore } from '@/store/artifacts.ts'

const props = defineProps({
  artifactId: { type: String, default: '' },
  hideTitle: { type: Boolean, default: false },
  hideProperties: { type: Boolean, default: false },
  hidePayload: { type: Boolean, default: false },
  hideActions: { type: Boolean, default: false },
})

const artifactStore = useArtifactStore()
const instanceStore = useInstanceStore()
const toast = useToast()
const { t } = useI18n({})
const { mobile } = useDisplay()

const fullscreenPayload = ref(false)
const showInfoDialog = ref(false)

const artifact = computed(() => artifactStore.getArtifact(props.artifactId))

const artifactTitle = computed(() => {
  return `${t('artifact.type.' + artifact.value.type)}, ${humanReadableTimestamp(artifact.value.timestamp)}`
})

const hasDuration = computed(() => {
  if (!artifact.value) return false
  if (!artifact.value.data) return false
  if (artifact.value.data.time) return true
  return !!artifact.value.data.durationSeconds
})

const humanReadableTime = computed(() => {
  if (!hasDuration.value) return null
  let start
  let end
  let duration
  if (artifact.value.data.time) {
    start = artifact.value.data.time.start
    end = artifact.value.data.time.end
    duration = parseDuration(artifact.value.data.time.duration)

    if (end === undefined) end = artifact.value.timestamp
    if (start === undefined) start = end - duration
    if (duration === undefined) duration = end - start
  } else {
    duration = artifact.value.data.durationSeconds * 1000
    end = artifact.value.timestamp
    start = end - duration
  }
  return `${humanReadableTimestamp(start)} - ${humanReadableTimestamp(end)} (${humanReadableDuration(duration)})`
})

const isDownloaded = computed(() => artifactStore.isPayloadDownloaded(props.artifactId))

const isDownloading = computed(() => artifactStore.isPayloadDownloading(props.artifactId))

const shareUrl = computed(() => {
  return `${getOrigin()}/instance/${instanceStore.selectedInstanceId}/artifact/${props.artifactId}`
})

async function downloadPayload() {
  await artifactStore.loadArtifactPayload(props.artifactId)
}

async function savePayload() {
  if (!isDownloaded.value) return
  await artifactStore.downloadArtifactPayloadAndSaveToFileSystem(props.artifactId)
}

async function downloadAndSavePayload() {
  await savePayload()
}

async function copyComponentId(e) {
  const copyResult = await writeToClipboard(props.artifactId)
  if (copyResult) {
    toast.success(t('toast.copied_to_clipboard'))
  } else {
    toast.error(t('toast.copy_failed'))
  }
  e.stopPropagation()
}

async function copyJSON() {
  const result = await writeToClipboard(JSON.stringify(artifact.value, null, 2))
  if (!result) {
    toast.error(t('toast.copy_failed'))
    return
  }
  toast.success(t('toast.copied_to_clipboard'))
}
</script>

<template>
  <v-layout-card
    v-if="artifact"
    :class="{ 'pa-0': props.hideTitle && props.hideProperties && props.hideActions }"
  >
    <div
      v-if="!props.hideTitle"
      class="px-2 d-flex flex-row justify-space-between align-center flex-wrap"
    >
      <v-card-title class="text-wrap">
        {{ artifactTitle }}
      </v-card-title>

      <v-btn
        class="rounded-circle"
        variant="text"
        size="small"
        icon
      >
        <v-icon icon="mdi-code-json" />
        <GenericCustomDialog
          v-model="showInfoDialog"
          activator="parent"
          max-width="800px"
          width="100%"
          card-class="pt-0"
          header-wrapper-class="pt-3 position-sticky top-0 w-100 bg-surface"
          hide-close-button
        >
          <template #headerActions>
            <div class="pa-2 d-flex justify-end ga-2">
              <v-btn
                class="rounded-circle"
                color="primary"
                icon
                variant="flat"
                size="x-small"
                @click="copyJSON"
              >
                <v-icon
                  icon="mdi-content-copy"
                  size="large"
                />
                <v-tooltip
                  activator="parent"
                  location="top"
                >
                  {{ $t('general_interface.buttons.copy') }}
                </v-tooltip>
              </v-btn>

              <v-btn
                class="rounded-circle"
                color="error"
                icon
                variant="flat"
                size="x-small"
                @click="showInfoDialog = false"
              >
                <v-icon
                  icon="mdi-close"
                  size="large"
                />
                <v-tooltip
                  activator="parent"
                  location="top"
                >
                  {{ $t('general_interface.buttons.close') }}
                </v-tooltip>
              </v-btn>
            </div>
          </template>
          <template #headerIcon>
            <v-icon
              icon="mdi-code-json"
              start
            />
          </template>
          <template #title>
            {{ artifactTitle }}
          </template>
          <template #body>
            <div>
              <pre style="overflow-x: auto"><code>{{ JSON.stringify(artifact, null, 2) }}</code></pre>
            </div>
          </template>
        </GenericCustomDialog>
      </v-btn>
    </div>
    <v-row class="mx-0">
      <!-- properties -->
      <v-col
        v-if="!props.hideProperties"
        cols="12"
        lg="4"
      >
        <v-list density="compact">
          <v-list-item
            :title="t('artifact.properties.status')"
            :subtitle="t('artifact.status.' + artifact.status)"
            prepend-icon="mdi-information"
          />
          <v-list-item
            v-if="artifact.data && artifact.data.cameraId"
            :title="t('artifact.properties.cameraId')"
            :subtitle="useCameraName(artifact.data.cameraId)"
            prepend-icon="mdi-video"
          />
          <v-list-item
            v-if="artifact.jobName"
            :title="t('artifact.properties.jobName')"
            :subtitle="artifact.jobName"
            prepend-icon="mdi-note-check"
          />
          <v-list-item
            v-if="hasDuration"
            :title="t('artifact.properties.time')"
            :subtitle="humanReadableTime"
            prepend-icon="mdi-timelapse"
          />
          <v-list-item
            v-if="artifact && artifact.data && artifact.data.frameIntervalSeconds"
            :title="t('artifact.properties.frameInterval')"
            :subtitle="humanReadableDuration(artifact.data.frameIntervalSeconds * 1000)"
            prepend-icon="mdi-timer"
          />
          <v-list-item
            v-if="artifact && artifact.size"
            :title="t('artifact.properties.filesize')"
            :subtitle="humanReadableFilesize(artifact.size)"
            prepend-icon="mdi-harddisk"
          />
          <v-list-item
            v-if="artifact.artifactId"
            prepend-icon="mdi-identifier"
            append-icon="mdi-content-copy"
            :title="$t('artifact.properties.id')"
            :subtitle="artifact.artifactId"
            @click="copyComponentId"
          />
        </v-list>
      </v-col>

      <!-- image -->
      <v-col
        :class="props.hideProperties ? 'pa-0' : ''"
        cols="12"
        :lg="props.hideProperties ? 12 : 8"
      >
        <v-card
          variant="flat"
          elevation="0"
        >
          <ArtifactPayload
            v-if="!props.hidePayload"
            :artifact-id="artifact.artifactId"
          />
          <v-card-actions
            v-if="!props.hideActions"
            class="px-0"
            :class="{
              'justify-space-between': !mobile,
              'justify-center': mobile,
            }"
          >
            <div class="d-flex justify-center align-center ga-2 flex-wrap">
              <v-btn
                v-if="!isDownloaded && (artifact?.status === 'done' || artifact?.status === 'archived')"
                prepend-icon="mdi-download"
                :disabled="isDownloading"
                color="primary"
                variant="flat"
                size="small"
                @click="downloadPayload"
              >
                {{ $t('general_interface.buttons.download') }}
              </v-btn>
              <v-btn
                v-if="isDownloaded"
                prepend-icon="mdi-content-save-outline"
                color="primary"
                variant="tonal"
                size="small"
                @click="savePayload"
              >
                {{ $t('general_interface.buttons.save') }}
              </v-btn>
              <v-btn
                v-if="!isDownloaded && (artifact?.status === 'done' || artifact?.status === 'archived')"
                prepend-icon="mdi-content-save-outline"
                color="primary"
                variant="tonal"
                size="small"
                :disabled="isDownloading"
                @click="downloadAndSavePayload"
              >
                {{ $t('general_interface.buttons.download_and_save') }}
              </v-btn>
              <ShareButton
                :url="shareUrl"
                variant="tonal"
                size="small"
                :text="$t('general_interface.buttons.share')"
              />
            </div>
            <v-btn
              v-if="isDownloaded"
              prepend-icon="mdi-fullscreen"
              variant="outlined"
              size="small"
            >
              {{ $t('general_interface.buttons.fullscreen') }}

              <GenericCustomDialog
                v-model="fullscreenPayload"
                activator="parent"
                width="100%"
                height="100dvh"
                :dialog-props="{ fullscreen: true }"
                body-class="flex-fill overflow-auto px-0"
                hide-cancel-button
              >
                <template #headerIcon>
                  <v-icon icon="mdi-image-area" />
                </template>
                <template #title>
                  {{ artifactTitle }}
                </template>
                <template #body>
                  <ArtifactPayload
                    v-if="fullscreenPayload"
                    :artifact-id="artifact.artifactId"
                    full-screen
                  />
                </template>
                <template #actions>
                  <v-btn
                    v-if="isDownloaded"
                    color="primary"
                    variant="tonal"
                    prepend-icon="mdi-content-save-outline"
                    @click="savePayload"
                  >
                    {{ $t('general_interface.buttons.save') }}
                  </v-btn>
                  <v-btn
                    prepend-icon="mdi-close"
                    color="primary"
                    variant="flat"
                    @click="fullscreenPayload = false"
                  >
                    {{ $t('general_interface.buttons.close') }}
                  </v-btn>
                </template>
              </GenericCustomDialog>
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-col>
    </v-row>
  </v-layout-card>
</template>
